import { useState, useEffect } from "react";
import { Lock, LogOut, X, FileText, Mail, Heart, GraduationCap, BookOpen, Users, Send } from "lucide-react";
import { Link, Navigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../auth/AuthContext";
import ResourceManager from "../components/admin/ResourceManager";
import TeamManager from "../components/admin/TeamManager";
import TestEmailModal from "../components/admin/TestEmailModal";
import ConsultationsManager from "../components/admin/ConsultationsManager";
import {
  LeadRowActions,
  NewPill,
  isRecordNew,
  unreadRowClass,
  markRead,
} from "../components/admin/LeadRowActions";

export const AdminDashboard = () => {
  const { user, loading, logout } = useAuth();
  const [intakes, setIntakes] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [insuranceInquiries, setInsuranceInquiries] = useState([]);
  const [insuranceConsultations, setInsuranceConsultations] = useState([]);
  const [educationInquiries, setEducationInquiries] = useState([]);
  const [resources, setResources] = useState([]);
  const [users, setUsers] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [selectedIntake, setSelectedIntake] = useState(null);
  const [selectedInsurance, setSelectedInsurance] = useState(null);
  const [selectedEducation, setSelectedEducation] = useState(null);
  const [activeTab, setActiveTab] = useState("intakes");
  const [showTestEmail, setShowTestEmail] = useState(false);

  // Sync tab from URL query (?tab=consultations) when opened from email link
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get("tab");
    if (tab && ["intakes", "messages", "insurance", "consultations", "education", "resources", "team"].includes(tab)) {
      setActiveTab(tab);
    }
  }, []);

  const fetchData = async () => {
    setDataLoading(true);
    try {
      const [intakesRes, consultationsRes, insuranceRes, insConsultRes, educationRes, resourcesRes, usersRes] = await Promise.all([
        api.get("/intake"),
        api.get("/consultation"),
        api.get("/insurance-inquiry"),
        api.get("/insurance-consultation"),
        api.get("/education-inquiry"),
        api.get("/resources"),
        api.get("/auth/users"),
      ]);
      setIntakes([...intakesRes.data].reverse());
      setConsultations([...consultationsRes.data].reverse());
      setInsuranceInquiries([...insuranceRes.data].reverse());
      setInsuranceConsultations(insConsultRes.data);
      setEducationInquiries([...educationRes.data].reverse());
      setResources(resourcesRes.data);
      setUsers(usersRes.data);
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setDataLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role === "admin") fetchData();
  }, [user]);

  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

  if (!user) {
    return <Navigate to="/login" state={{ from: "/admin" }} replace />;
  }

  if (user.role !== "admin") {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="bg-white p-8 max-w-md text-center">
          <Lock className="text-slate-300 mx-auto mb-4" size={48} />
          <h1 className="font-serif text-2xl font-bold text-slate-900 mb-2">Access Denied</h1>
          <p className="text-slate-500 mb-6">You need an admin account to view this page.</p>
          <Link to="/portal" className="text-[#C89B3C] hover:underline">Go to Client Portal</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="bg-slate-900 text-white py-4 px-6 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-[#C89B3C] flex items-center justify-center">
              <span className="text-slate-900 font-bold text-xl font-serif">N</span>
            </div>
            <div>
              <h1 className="font-serif font-bold">Admin Dashboard</h1>
              <p className="text-slate-400 text-xs">Signed in as {user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowTestEmail(true)}
              data-testid="admin-test-email-btn"
              className="text-slate-300 hover:text-white text-sm inline-flex items-center gap-1"
              title="Send a test email to verify SMTP delivery"
            >
              <Send size={14} /> Test Email
            </button>
            <button
              onClick={fetchData}
              className="text-slate-300 hover:text-white text-sm"
            >
              Refresh
            </button>
            <button
              onClick={logout}
              className="flex items-center gap-2 text-slate-300 hover:text-white"
              data-testid="admin-logout"
            >
              <LogOut size={18} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-white p-6 border-l-4 border-[#C89B3C]">
            <h3 className="text-slate-500 text-sm">Veteran Intakes</h3>
            <p className="font-serif text-3xl font-bold text-slate-900">{intakes.length}</p>
          </div>
          <div className="bg-white p-6 border-l-4 border-slate-900">
            <h3 className="text-slate-500 text-sm">Quick Messages</h3>
            <p className="font-serif text-3xl font-bold text-slate-900">{consultations.length}</p>
          </div>
          <div className="bg-white p-6 border-l-4 border-[#0B2343]">
            <h3 className="text-slate-500 text-sm">Insurance Inquiries</h3>
            <p className="font-serif text-3xl font-bold text-slate-900">{insuranceInquiries.length}</p>
          </div>
          <div className="bg-white p-6 border-l-4 border-[#C89B3C]">
            <h3 className="text-slate-500 text-sm">Consultations</h3>
            <p className="font-serif text-3xl font-bold text-slate-900">{insuranceConsultations.length}</p>
          </div>
          <div className="bg-white p-6 border-l-4 border-emerald-600">
            <h3 className="text-slate-500 text-sm">Education Inquiries</h3>
            <p className="font-serif text-3xl font-bold text-slate-900">{educationInquiries.length}</p>
          </div>
        </div>

        <div className="flex gap-2 mb-4 flex-wrap">
          <TabBtn active={activeTab === "intakes"} onClick={() => setActiveTab("intakes")} testId="admin-tab-intakes">
            Veteran Intakes ({intakes.length})
          </TabBtn>
          <TabBtn active={activeTab === "messages"} onClick={() => setActiveTab("messages")} testId="admin-tab-messages">
            Quick Messages ({consultations.length})
          </TabBtn>
          <TabBtn active={activeTab === "insurance"} onClick={() => setActiveTab("insurance")} testId="admin-tab-insurance">
            Insurance Inquiries ({insuranceInquiries.length})
          </TabBtn>
          <TabBtn active={activeTab === "consultations"} onClick={() => setActiveTab("consultations")} testId="admin-tab-consultations">
            Consultations ({insuranceConsultations.length})
          </TabBtn>
          <TabBtn active={activeTab === "education"} onClick={() => setActiveTab("education")} testId="admin-tab-education">
            Education Inquiries ({educationInquiries.length})
          </TabBtn>
          <TabBtn active={activeTab === "resources"} onClick={() => setActiveTab("resources")} testId="admin-tab-resources">
            Resources CMS ({resources.length})
          </TabBtn>
          <TabBtn active={activeTab === "team"} onClick={() => setActiveTab("team")} testId="admin-tab-team">
            Team ({users.length})
          </TabBtn>
        </div>

        {dataLoading ? (
          <div className="text-center py-12">
            <p className="text-slate-500">Loading...</p>
          </div>
        ) : (
          <>
            {activeTab === "intakes" && (
              <div className="bg-white">
                {intakes.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="mx-auto text-slate-300 mb-4" size={48} />
                    <p className="text-slate-500">No intake forms yet</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {intakes.map((intake) => (
                      <div
                        key={intake.id}
                        className={`p-4 hover:bg-slate-50 cursor-pointer transition-colors ${unreadRowClass(intake)}`}
                        onClick={() => {
                          if (isRecordNew(intake)) markRead("intakes", intake.id).then(fetchData);
                          setSelectedIntake(intake);
                        }}
                        data-testid={`admin-intake-row-${intake.id}`}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h3 className={`text-slate-900 ${isRecordNew(intake) ? "font-bold" : "font-medium"}`}>
                                {intake.fullName}
                              </h3>
                              {isRecordNew(intake) && <NewPill />}
                            </div>
                            <p className="text-slate-500 text-sm">{intake.email}</p>
                            <p className="text-slate-400 text-sm mt-1">
                              {intake.branch} • {intake.cityState}
                            </p>
                          </div>
                          <div className="flex items-start gap-3">
                            <div className="text-right">
                              <span className="text-xs text-slate-400">{formatDate(intake.created_at)}</span>
                              <p className="text-sm text-[#C89B3C] mt-1">{intake.timeline}</p>
                            </div>
                            <LeadRowActions
                              collection="intakes"
                              recordId={intake.id}
                              isRead={intake.isRead !== false}
                              onChanged={fetchData}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === "messages" && (
              <div className="bg-white">
                {consultations.length === 0 ? (
                  <div className="text-center py-12">
                    <Mail className="mx-auto text-slate-300 mb-4" size={48} />
                    <p className="text-slate-500">No messages yet</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {consultations.map((msg) => (
                      <div
                        key={msg.id}
                        className={`p-4 ${unreadRowClass(msg)}`}
                        data-testid={`admin-message-row-${msg.id}`}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h3 className={`text-slate-900 ${isRecordNew(msg) ? "font-bold" : "font-medium"}`}>
                                {msg.fullName}
                              </h3>
                              {isRecordNew(msg) && <NewPill />}
                            </div>
                            <p className="text-slate-500 text-sm">
                              {msg.email} • {msg.phone || "No phone"}
                            </p>
                          </div>
                          <div className="flex items-start gap-3">
                            <span className="text-xs text-slate-400">{formatDate(msg.created_at)}</span>
                            <LeadRowActions
                              collection="consultations"
                              recordId={msg.id}
                              isRead={msg.isRead !== false}
                              onChanged={fetchData}
                            />
                          </div>
                        </div>
                        <p className="text-slate-600 text-sm bg-slate-50 p-3 mt-2">
                          {msg.message || "No message"}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === "insurance" && (
              <div className="bg-white">
                {insuranceInquiries.length === 0 ? (
                  <div className="text-center py-12">
                    <Heart className="mx-auto text-slate-300 mb-4" size={48} />
                    <p className="text-slate-500">No insurance inquiries yet</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {insuranceInquiries.map((inq) => (
                      <div
                        key={inq.id}
                        className={`p-4 hover:bg-slate-50 cursor-pointer transition-colors ${unreadRowClass(inq)}`}
                        onClick={() => {
                          if (isRecordNew(inq)) markRead("insurance-inquiries", inq.id).then(fetchData);
                          setSelectedInsurance(inq);
                        }}
                        data-testid={`admin-insurance-row-${inq.id}`}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h3 className={`text-slate-900 ${isRecordNew(inq) ? "font-bold" : "font-medium"}`}>
                                {inq.firstName} {inq.lastName}
                              </h3>
                              {isRecordNew(inq) && <NewPill />}
                            </div>
                            <p className="text-slate-500 text-sm">
                              {inq.email} • {inq.phone}
                            </p>
                            <p className="text-slate-400 text-sm mt-1">
                              {inq.state} • {inq.veteranStatus}
                            </p>
                          </div>
                          <div className="flex items-start gap-3">
                            <div className="text-right">
                              <span className="text-xs text-slate-400">{formatDate(inq.created_at)}</span>
                              <p className="text-sm text-[#C89B3C] mt-1">{inq.serviceInterest}</p>
                            </div>
                            <LeadRowActions
                              collection="insurance-inquiries"
                              recordId={inq.id}
                              isRead={inq.isRead !== false}
                              onChanged={fetchData}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === "education" && (
              <div className="bg-white">
                {educationInquiries.length === 0 ? (
                  <div className="text-center py-12">
                    <GraduationCap className="mx-auto text-slate-300 mb-4" size={48} />
                    <p className="text-slate-500">No education inquiries yet</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {educationInquiries.map((inq) => (
                      <div
                        key={inq.id}
                        className={`p-4 hover:bg-slate-50 cursor-pointer transition-colors ${unreadRowClass(inq)}`}
                        onClick={() => {
                          if (isRecordNew(inq)) markRead("education-inquiries", inq.id).then(fetchData);
                          setSelectedEducation(inq);
                        }}
                        data-testid={`admin-education-row-${inq.id}`}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h3 className={`text-slate-900 ${isRecordNew(inq) ? "font-bold" : "font-medium"}`}>
                                {inq.firstName} {inq.lastName}
                              </h3>
                              {isRecordNew(inq) && <NewPill />}
                            </div>
                            <p className="text-slate-500 text-sm">
                              {inq.email} • {inq.phone}
                            </p>
                            <p className="text-slate-400 text-sm mt-1">
                              {inq.militaryStatus} • {inq.currentEducation || "Education not specified"}
                            </p>
                          </div>
                          <div className="flex items-start gap-3">
                            <div className="text-right">
                              <span className="text-xs text-slate-400">{formatDate(inq.created_at)}</span>
                              <p className="text-sm text-[#C89B3C] mt-1">{inq.programInterest}</p>
                            </div>
                            <LeadRowActions
                              collection="education-inquiries"
                              recordId={inq.id}
                              isRead={inq.isRead !== false}
                              onChanged={fetchData}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === "resources" && (
              <ResourceManager resources={resources} refresh={fetchData} />
            )}

            {activeTab === "consultations" && (
              <ConsultationsManager consultations={insuranceConsultations} refresh={fetchData} />
            )}

            {activeTab === "team" && (
              <TeamManager users={users} refresh={fetchData} />
            )}
          </>
        )}
      </div>

      {selectedInsurance && (
        <div
          className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 overflow-y-auto"
          onClick={() => setSelectedInsurance(null)}
        >
          <div
            className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto my-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-[#0B2343] p-6 sticky top-0">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="font-serif text-xl font-bold text-white">
                    {selectedInsurance.firstName} {selectedInsurance.lastName}
                  </h2>
                  <p className="text-[#C89B3C] text-sm">
                    {selectedInsurance.serviceInterest} • {selectedInsurance.state}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedInsurance(null)}
                  className="text-slate-400 hover:text-white"
                  data-testid="admin-insurance-close"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              <Section title="Contact Information">
                <Grid2>
                  <Item label="Email">
                    <a href={`mailto:${selectedInsurance.email}`} className="text-[#C89B3C]">
                      {selectedInsurance.email}
                    </a>
                  </Item>
                  <Item label="Phone">{selectedInsurance.phone}</Item>
                  <Item label="Preferred Contact">{selectedInsurance.preferredContact}</Item>
                  <Item label="Best Time">{selectedInsurance.bestTimeToContact || "Any time"}</Item>
                </Grid2>
              </Section>

              <Section title="Personal Details">
                <Grid2>
                  <Item label="State">{selectedInsurance.state}</Item>
                  <Item label="Date of Birth">{selectedInsurance.dateOfBirth || "Not provided"}</Item>
                  <Item label="Veteran Status">{selectedInsurance.veteranStatus}</Item>
                  <Item label="Service Interest">
                    <span className="text-[#C89B3C] font-medium">
                      {selectedInsurance.serviceInterest}
                    </span>
                  </Item>
                </Grid2>
              </Section>

              {selectedInsurance.additionalComments && (
                <Section title="Additional Comments">
                  <p className="text-sm bg-slate-50 p-3">{selectedInsurance.additionalComments}</p>
                </Section>
              )}

              <div className="text-xs text-slate-400 pt-4 border-t">
                Submitted: {formatDate(selectedInsurance.created_at)}
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedIntake && (
        <div
          className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 overflow-y-auto"
          onClick={() => setSelectedIntake(null)}
        >
          <div
            className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto my-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-slate-900 p-6 sticky top-0">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="font-serif text-xl font-bold text-white">
                    {selectedIntake.fullName}
                  </h2>
                  <p className="text-[#C89B3C] text-sm">
                    {selectedIntake.branch} • {selectedIntake.datesOfService}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedIntake(null)}
                  className="text-slate-400 hover:text-white"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              <Section title="Contact Information">
                <Grid2>
                  <Item label="Email">
                    <a href={`mailto:${selectedIntake.email}`} className="text-[#C89B3C]">
                      {selectedIntake.email}
                    </a>
                  </Item>
                  <Item label="Phone">{selectedIntake.phone}</Item>
                  <div className="col-span-2">
                    <span className="text-slate-500 text-sm">Location:</span>
                    <br />
                    {selectedIntake.cityState}
                  </div>
                </Grid2>
              </Section>

              <Section title="Military Service">
                <Grid2>
                  <Item label="Branch">{selectedIntake.branch}</Item>
                  <Item label="Dates">{selectedIntake.datesOfService}</Item>
                  <Item label="MOS/Rate/AFSC">{selectedIntake.mosRateAfsc}</Item>
                  <Item label="Deployments">{selectedIntake.deployments || "None"}</Item>
                </Grid2>
              </Section>

              <Section title="VA Claim Status">
                <Grid2>
                  <Item label="Filed Before">{selectedIntake.filedBefore}</Item>
                  <Item label="Current Rating">{selectedIntake.currentRating || "None"}</Item>
                  <div className="col-span-2">
                    <span className="text-slate-500 text-sm">Seeking Help With:</span>
                    <br />
                    {selectedIntake.seekingHelp?.join(", ") || "Not specified"}
                  </div>
                </Grid2>
              </Section>

              <Section title="Conditions">
                <p className="text-sm bg-slate-50 p-3">{selectedIntake.conditions}</p>
              </Section>

              <Section title="Documentation & Timeline">
                <Grid2>
                  <Item label="Has Records">{selectedIntake.hasRecords}</Item>
                  <Item label="Receiving VA Care">{selectedIntake.receivingVaCare}</Item>
                  <Item label="Timeline">
                    <span className="text-[#C89B3C] font-medium">{selectedIntake.timeline}</span>
                  </Item>
                  <Item label="Referred By">{selectedIntake.referredBy || "Not specified"}</Item>
                </Grid2>
              </Section>

              <div className="text-xs text-slate-400 pt-4 border-t">
                Submitted: {formatDate(selectedIntake.created_at)}
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedEducation && (
        <div
          className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 overflow-y-auto"
          onClick={() => setSelectedEducation(null)}
        >
          <div
            className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto my-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-[#0B2343] p-6 sticky top-0">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="font-serif text-xl font-bold text-white">
                    {selectedEducation.firstName} {selectedEducation.lastName}
                  </h2>
                  <p className="text-[#C89B3C] text-sm">
                    {selectedEducation.programInterest} • {selectedEducation.militaryStatus}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedEducation(null)}
                  className="text-slate-400 hover:text-white"
                  data-testid="admin-education-close"
                >
                  <X size={24} />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-6">
              <Section title="Contact">
                <Grid2>
                  <Item label="Email">
                    <a href={`mailto:${selectedEducation.email}`} className="text-[#C89B3C]">
                      {selectedEducation.email}
                    </a>
                  </Item>
                  <Item label="Phone">{selectedEducation.phone}</Item>
                  <Item label="Best Time">{selectedEducation.bestTimeToContact || "Any time"}</Item>
                  <Item label="Timeline">{selectedEducation.timeline || "Not specified"}</Item>
                </Grid2>
              </Section>
              <Section title="Details">
                <Grid2>
                  <Item label="Military Status">{selectedEducation.militaryStatus}</Item>
                  <Item label="Current Education">
                    {selectedEducation.currentEducation || "Not specified"}
                  </Item>
                  <Item label="Program Interest">
                    <span className="text-[#C89B3C] font-medium">{selectedEducation.programInterest}</span>
                  </Item>
                </Grid2>
              </Section>
              <Section title="Goal">
                <p className="text-sm bg-slate-50 p-3">{selectedEducation.goal}</p>
              </Section>
              <div className="text-xs text-slate-400 pt-4 border-t">
                Submitted: {formatDate(selectedEducation.created_at)}
              </div>
            </div>
          </div>
        </div>
      )}

      <TestEmailModal
        isOpen={showTestEmail}
        onClose={() => setShowTestEmail(false)}
        defaultTo={user?.email}
      />
    </div>
  );
};

const TabBtn = ({ active, onClick, testId, children }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`px-4 py-2 font-medium transition-colors ${
      active
        ? "bg-slate-900 text-white"
        : "bg-white text-slate-600 hover:bg-slate-50"
    }`}
  >
    {children}
  </button>
);


const Section = ({ title, children }) => (
  <div>
    <h3 className="font-bold text-slate-900 border-b border-[#C89B3C] pb-2 mb-3">{title}</h3>
    {children}
  </div>
);

const Grid2 = ({ children }) => (
  <div className="grid grid-cols-2 gap-4 text-sm">{children}</div>
);

const Item = ({ label, children }) => (
  <div>
    <span className="text-slate-500">{label}:</span>
    <br />
    {children}
  </div>
);

export default AdminDashboard;
